import { fieldsForItem } from "@plannello/core/fields";
import { m } from "@plannello/i18n/messages";
import { type ItemLite, type ItemPatch, localizedNames } from "@plannello/shared";
import { PRIORITY_VALUES, PriorityIcon, priorityLabel, priorityWord } from "@/components/domain/PriorityIcon";
import type { PickerOption } from "@/components/domain/PropertyPicker";
import { StatusIcon } from "@/components/domain/StatusIcon";
import { TypeIcon } from "@/components/domain/TypeIcon";
import { Avatar } from "@/components/ui";
import type { Catalog } from "@/data/catalog";
import { updateItems } from "@/data/mutations";
import { findLite } from "@/data/store";
import { currentDisplayTitle } from "@/data/titles";
import type { SchemaStatus } from "@/data/types";
import { pushUndo } from "@/data/undo";
import { labelSwatch, loc } from "@/lib/format";
import { toast } from "@/lib/toast";

export type PickerKind =
  | "status"
  | "priority"
  | "assignee"
  | "labels"
  | "type"
  | "parent"
  | "milestone"
  | "product"
  | `field:${string}`;

export const NONE = "__none";

export type PickerContext = {
  catalog: Catalog;
  meId: string | null;
  /** Cached rows used for parent candidates. */
  items: readonly ItemLite[];
};

export interface PickerModel {
  label: string;
  shortcut?: string;
  mode: "single" | "multiple";
  options: PickerOption[];
  selected: Set<string>;
  pick?: (id: string) => void;
  toggle?: (id: string, on: boolean) => void;
  /** Multiple mode: called when the picker closes (offers one undo for the whole session). */
  finish?: () => void;
}

const keywords = (name: Parameters<typeof localizedNames>[0], slug?: string) =>
  [...localizedNames(name), slug ?? ""].join(" ");

const swatch = (color: string | undefined) => (
  <span aria-hidden="true" className="size-2 rounded-full" style={{ backgroundColor: labelSwatch(color) }} />
);

/* ------------------------------------------------------------------ options */

export function statusOptions(statuses: SchemaStatus[]): PickerOption[] {
  return statuses.map((s, i) => ({
    id: s.id,
    label: loc(s.name),
    keywords: keywords(s.name, s.slug),
    icon: <StatusIcon category={s.category} decorative />,
    shortcut: i < 9 ? String(i + 1) : undefined,
  }));
}

export function priorityOptions(): PickerOption[] {
  return PRIORITY_VALUES.map((v) => ({
    id: String(v),
    label: priorityLabel(v),
    description: v === 0 || priorityLabel(v) === priorityWord(v) ? undefined : priorityWord(v),
    keywords: `p${v - 1} ${priorityWord(v)}`,
    icon: <PriorityIcon value={v} />,
    shortcut: String(v),
  }));
}

export function typeOptions(catalog: Catalog): PickerOption[] {
  return catalog.types.map((t) => ({
    id: t.id,
    label: loc(t.name),
    keywords: keywords(t.name, t.slug),
    icon: <TypeIcon name={t.icon} color={t.color} decorative />,
  }));
}

export function assigneeOptions(catalog: Catalog, meId: string | null): PickerOption[] {
  const users = [...catalog.users].sort((a, b) => Number(b.id === meId) - Number(a.id === meId));
  return users.map((u) => ({
    id: u.id,
    label: u.id === meId ? m.picker_you({ name: u.name }) : u.name,
    keywords: `${u.email ?? ""} ${u.id === meId ? "me" : ""}`,
    icon: <Avatar name={u.name} seed={u.id} src={u.image} bot={u.kind === "bot"} size="xs" labelled={false} />,
  }));
}

export function productOptions(catalog: Catalog): PickerOption[] {
  return catalog.spaces.flatMap((s) =>
    s.products.map((p) => ({
      id: p.id,
      label: p.isSpaceProduct ? `${s.name}` : p.name,
      description: p.key,
      keywords: `${p.key} ${s.name}`,
      section: s.name,
    })),
  );
}

export function milestoneOptions(catalog: Catalog, productId: string): PickerOption[] {
  const ms = catalog.schemaOf(productId)?.milestones ?? [];
  return [
    { id: NONE, label: m.prop_no_milestone() },
    ...ms
      .filter((x) => x.status === "open")
      .map((x) => ({ id: x.id, label: x.name, description: x.version ?? undefined })),
  ];
}

/** Fields (single and multi select) applying to all targets, plus the plain labels. */
export function labelOptions(
  catalog: Catalog,
  targets: readonly { productId: string; typeId: string }[],
  onlyGroupId?: string,
) {
  const first = targets[0];
  if (!first) return [];
  const schema = catalog.schemaOf(first.productId);
  if (!schema) return [];
  const fields = fieldsForItem(schema, first.typeId, first.productId).filter(
    (f) =>
      (!onlyGroupId || f.id === onlyGroupId) &&
      targets.every(
        (t) =>
          t === first ||
          fieldsForItem(catalog.schemaOf(t.productId) ?? schema, t.typeId, t.productId).some((g) => g.id === f.id),
      ),
  );
  const out: PickerOption[] = [];
  for (const f of fields) {
    for (const l of f.labels) {
      out.push({
        id: l.id,
        label: loc(l.name),
        keywords: keywords(l.name, l.slug),
        icon: swatch(l.color),
        section: loc(f.name),
      });
    }
  }
  if (!onlyGroupId) {
    for (const l of schema.labels.filter((x) => !x.groupId)) {
      out.push({
        id: l.id,
        label: loc(l.name),
        keywords: keywords(l.name, l.slug),
        icon: swatch(l.color),
        section: m.prop_labels(),
      });
    }
  }
  return out;
}

/** Items that may become the parent of every target (same space, higher level, allowed type, no cycle). */
export function parentCandidates(ctx: PickerContext, targets: readonly ItemLite[]): ItemLite[] {
  const { catalog, items } = ctx;
  const targetIds = new Set(targets.map((t) => t.id));
  const childrenOf = new Map<string, string[]>();
  for (const i of items) if (i.parentId) childrenOf.set(i.parentId, [...(childrenOf.get(i.parentId) ?? []), i.id]);
  const excluded = new Set<string>(targetIds);
  const stack = [...targetIds];
  while (stack.length) {
    for (const c of childrenOf.get(stack.pop()!) ?? []) {
      if (!excluded.has(c)) {
        excluded.add(c);
        stack.push(c);
      }
    }
  }
  const productSpace = (pid: string) => catalog.productById.get(pid)?.spaceId;
  const spaces = new Set(targets.map((t) => productSpace(t.productId)));
  return items.filter((i) => {
    if (excluded.has(i.id) || i.archivedAt) return false;
    if (!spaces.has(productSpace(i.productId))) return false;
    const pt = catalog.typeById.get(i.typeId);
    if (!pt) return false;
    return targets.every((t) => {
      const tt = catalog.typeById.get(t.typeId);
      if (!tt) return false;
      return pt.level > tt.level && (tt.allowedParentTypeIds.length === 0 || tt.allowedParentTypeIds.includes(pt.id));
    });
  });
}

export function parentOptions(ctx: PickerContext, targets: readonly ItemLite[]): PickerOption[] {
  const { catalog } = ctx;
  return [
    { id: NONE, label: m.prop_no_parent() },
    ...parentCandidates(ctx, targets).map((i) => {
      const t = catalog.typeById.get(i.typeId);
      return {
        id: i.id,
        label: currentDisplayTitle(i).text,
        prefix: i.key,
        icon: t ? <TypeIcon name={t.icon} color={t.color} decorative /> : undefined,
      };
    }),
  ];
}

/* ------------------------------------------------------------------ commit helpers */

/** The status of `target`'s workflow that corresponds to `chosen` (same id, slug or category). */
export function mapStatus(catalog: Catalog, target: ItemLite, chosen: SchemaStatus): string {
  const wf = catalog.productById.get(target.productId)?.workflowId;
  if (!wf || chosen.workflowId === wf) return chosen.id;
  const list = catalog.statusesOf(wf);
  return (list.find((s) => s.slug === chosen.slug) ?? list.find((s) => s.category === chosen.category) ?? chosen).id;
}

const intersect = (lists: string[][]) => {
  if (lists.length === 0) return new Set<string>();
  const [first, ...rest] = lists;
  return new Set(first!.filter((x) => rest.every((l) => l.includes(x))));
};
const allSame = <T,>(xs: T[]) => (xs.length > 0 && xs.every((x) => x === xs[0]) ? xs[0] : undefined);

/** Labels of the group `groupId` (for exclusive groups). */
const groupLabelIds = (catalog: Catalog, groupId: string) =>
  catalog.labels.filter((l) => l.groupId === groupId).map((l) => l.id);

function sessionUndo(targets: readonly ItemLite[], field: "assigneeIds" | "labelIds", title: string) {
  const before = new Map(targets.map((t) => [t.id, t[field]]));
  return () => {
    const changed = targets.some((t) => {
      const now = findLite(t.id)?.[field] ?? t[field];
      const was = before.get(t.id) ?? [];
      return now.length !== was.length || now.some((x) => !was.includes(x));
    });
    if (!changed) return;
    const restore = () =>
      void updateItems(
        targets.map((t) => findLite(t.id) ?? t),
        (item) => {
          const was = before.get(item.id) ?? [];
          const now = item[field];
          const op = { add: was.filter((x) => !now.includes(x)), remove: now.filter((x) => !was.includes(x)) };
          return field === "assigneeIds" ? { assignees: op } : { labels: op };
        },
      );
    toast.undo(title, pushUndo(restore), m.common_undo());
  };
}

/** Picker for `kind` that edits `targets` (lists, boards, the detail sidebar, bulk bar). */
export function buildPickerModel(
  kind: PickerKind,
  targets: readonly ItemLite[],
  ctx: PickerContext,
): PickerModel | null {
  const { catalog } = ctx;
  const first = targets[0];
  if (!first) return null;
  const single = (
    label: string,
    shortcut: string | undefined,
    options: PickerOption[],
    selected: string | undefined,
    pick: (id: string) => void,
  ): PickerModel => ({
    label,
    shortcut,
    mode: "single",
    options,
    selected: new Set(selected ? [selected] : []),
    pick,
  });

  switch (kind) {
    case "status": {
      const wf = catalog.productById.get(first.productId)?.workflowId ?? "";
      const statuses = catalog.statusesOf(wf);
      return single(m.cmd_status(), "S", statusOptions(statuses), allSame(targets.map((t) => t.statusId)), (id) => {
        const chosen = catalog.statusById.get(id);
        if (!chosen) return;
        void updateItems(targets, (item) => ({ statusId: mapStatus(catalog, item, chosen) }), {
          undoTitle: m.toast_status_set({ status: loc(chosen.name) }),
        });
      });
    }
    case "priority":
      return single(m.cmd_priority(), "P", priorityOptions(), allSame(targets.map((t) => String(t.priority))), (id) => {
        const v = Number(id);
        void updateItems(targets, () => ({ priority: v }), {
          undoTitle: m.toast_priority_set({ priority: priorityLabel(v) }),
        });
      });
    case "type":
      return single(m.cmd_type(), "T", typeOptions(catalog), allSame(targets.map((t) => t.typeId)), (id) => {
        const t = catalog.typeById.get(id);
        void updateItems(targets, () => ({ typeId: id }), { undoTitle: m.toast_type_set({ type: loc(t?.name) }) });
      });
    case "milestone":
      return single(
        m.cmd_milestone(),
        undefined,
        milestoneOptions(catalog, first.productId),
        allSame(targets.map((t) => t.milestoneId ?? NONE)),
        (id) =>
          void updateItems(targets, () => ({ milestoneId: id === NONE ? null : id }), {
            undoTitle: m.toast_milestone_changed(),
          }),
      );
    case "parent":
      return single(
        m.cmd_parent(),
        "M",
        parentOptions(ctx, targets),
        allSame(targets.map((t) => t.parentId ?? NONE)),
        (id) =>
          void updateItems(targets, () => ({ parentId: id === NONE ? null : id }), {
            undoTitle: m.toast_parent_changed(),
          }),
      );
    case "assignee": {
      const finish = sessionUndo(targets, "assigneeIds", m.toast_assignees_changed());
      return {
        label: m.cmd_assignee(),
        shortcut: "A",
        mode: "multiple",
        options: assigneeOptions(catalog, ctx.meId),
        selected: intersect(targets.map((t) => findLite(t.id)?.assigneeIds ?? t.assigneeIds)),
        toggle: (id, on) =>
          void updateItems(targets, (item): ItemPatch | null =>
            on
              ? item.assigneeIds.includes(id)
                ? null
                : { assignees: { add: [id] } }
              : item.assigneeIds.includes(id)
                ? { assignees: { remove: [id] } }
                : null,
          ),
        finish,
      };
    }
    default: {
      const groupId = kind.startsWith("field:") ? kind.slice(6) : undefined;
      const group = groupId ? catalog.groupById.get(groupId) : undefined;
      const options = labelOptions(catalog, targets, groupId);
      const current = () => targets.map((t) => findLite(t.id)?.labelIds ?? t.labelIds);
      const toggleLabel = (id: string, on: boolean) => {
        const label = catalog.labelById.get(id);
        const g = label?.groupId ? catalog.groupById.get(label.groupId) : undefined;
        const siblings = g?.exclusive ? groupLabelIds(catalog, g.id).filter((x) => x !== id) : [];
        void updateItems(targets, (item): ItemPatch | null => {
          if (on) {
            const remove = siblings.filter((x) => item.labelIds.includes(x));
            return item.labelIds.includes(id) && remove.length === 0 ? null : { labels: { add: [id], remove } };
          }
          return item.labelIds.includes(id) ? { labels: { remove: [id] } } : null;
        });
      };
      if (group?.exclusive) {
        const ids = new Set(groupLabelIds(catalog, group.id));
        const values = current().map((l) => l.find((x) => ids.has(x)) ?? NONE);
        return single(
          m.picker_placeholder({ property: loc(group.name) }),
          undefined,
          [{ id: NONE, label: m.no_field_value({ field: loc(group.name) }) }, ...options],
          allSame(values),
          (id) => {
            if (id === NONE)
              void updateItems(targets, (item) => ({ labels: { remove: item.labelIds.filter((x) => ids.has(x)) } }), {
                undoTitle: m.toast_labels_changed(),
              });
            else {
              const siblings = [...ids].filter((x) => x !== id);
              void updateItems(
                targets,
                (item) => ({ labels: { add: [id], remove: siblings.filter((x) => item.labelIds.includes(x)) } }),
                { undoTitle: m.toast_labels_changed() },
              );
            }
          },
        );
      }
      return {
        label: group ? m.picker_placeholder({ property: loc(group.name) }) : m.cmd_labels(),
        shortcut: group ? undefined : "L",
        mode: "multiple",
        options,
        selected: intersect(current()),
        toggle: toggleLabel,
        finish: sessionUndo(targets, "labelIds", m.toast_labels_changed()),
      };
    }
  }
}
